/**
 * Profile Domain Store
 *
 * Manages user identity, role, onboarding progress, and profile data
 * for both startup founders and investors.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';
import { reportError } from '@/lib/errors';

import type { ZefixCompanyData, StartuptickerProfileData, WebsiteEnrichmentData } from '@/lib/supabase/edge-functions';
import type { AuthUserId } from '@/lib/ids';

// ============================================================================
// Types
// ============================================================================

export type UserRole = 'startup' | 'investor' | 'admin' | 'pending' | null;

export type OnboardingStep =
  | 'welcome'
  | 'profile-method'
  | 'profile-basic'
  | 'profile-details'
  | 'data-room'
  | 'fundraising'
  | 'linkedin'
  | 'investment-history'
  | 'portfolio'
  | 'complete';

export interface OnboardingProgress {
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
  skippedSteps: OnboardingStep[];
  profileMethod?: 'deck' | 'questionnaire';
  startedAt: string;
  completedAt?: string;
}

export const STARTUP_REQUIRED_STEPS: OnboardingStep[] = ['welcome', 'profile-basic'];
export const STARTUP_OPTIONAL_STEPS: OnboardingStep[] = ['profile-method', 'profile-details', 'data-room', 'fundraising'];

export const INVESTOR_REQUIRED_STEPS: OnboardingStep[] = ['welcome', 'linkedin', 'profile-basic'];
export const INVESTOR_OPTIONAL_STEPS: OnboardingStep[] = ['investment-history', 'portfolio'];

export const VOICE_INPUT_STEPS: OnboardingStep[] = ['profile-basic', 'profile-details', 'fundraising', 'investment-history'];

export interface FundraisingRound {
  id: string;
  stage: 'pre-seed' | 'seed' | 'series-a' | 'series-b' | 'other';
  amount: number;
  date: string;
  leadInvestor?: string;
  verified: boolean;
  verifiedBy?: string;
}

export interface InvestmentRecord {
  id: string;
  companyName: string;
  industry?: string;
  stage: 'pre-seed' | 'seed' | 'bridge' | 'series-a' | 'series-b' | 'series-c+';
  investmentType: 'direct' | 'syndicate';
  /**
   * For syndicate investments, the lead the founder actually dealt with.
   *
   * A syndicate participant is usually a name the founder has never seen — the
   * cheque, the negotiation and the cap-table line sit with the lead. Without
   * this, portfolio verification asks a founder about someone they cannot
   * recognise, and a CORRECT claim gets rejected as a false one.
   */
  syndicateLeadName?: string;
  securityType?: 'safe' | 'convertible-note' | 'equity' | 'other';
  date: string;
  investmentAmount?: number;
  valuationAtInvestment?: number;
  currentValuation?: number;
  ownership?: number;
  status?: 'active' | 'exited' | 'watchlist' | 'at_risk';
  investorRole?: 'none' | 'board-member' | 'board-observer' | 'advisor';
  lastUpdate?: string;
  metrics?: {
    revenue?: number;
    growth?: number;
    runway?: number;
    employees?: number;
  };
  source: 'linkedin' | 'manual';
}

export interface PortfolioClaim {
  id: string;
  companyName: string;
  website?: string;
  founderEmail?: string;
  claimDate: string;
  status: 'pending' | 'verified' | 'rejected';
  verifiedAt?: string;
}

export interface VerificationRequest {
  id: string;
  investorId: string;
  investorName: string;
  investorFirm?: string;
  claimId: string;
  requestDate: string;
  status: 'pending' | 'verified' | 'rejected';
  respondedAt?: string;
}

export type UpdateVisibility = 'public' | 'data_room' | 'investors_only';

export interface StartupUpdate {
  id: string;
  type: 'milestone' | 'traction' | 'funding' | 'team' | 'product' | 'announcement';
  title: string;
  summary: string;
  externalLink?: string;
  date: string;
  createdBy: string;
  visibility: UpdateVisibility;
}

export interface StartupProfile {
  id: string;
  name: string;
  tagline: string;
  description?: string;
  /**
   * Narrative "About" published from the scoring-interview digest (the `about`
   * DB column, migration 20260708215437). Distinct from `description`, which is
   * a store-only field with no DB column. The scorecard's About slot reads this.
   */
  about?: string;
  industry: string;
  stage: 'pre-seed' | 'seed' | 'series-a';
  fundingTarget: number;
  location: string;
  foundedYear: number;
  teamSize: number;
  website?: string;
  linkedIn?: string;
  founderName?: string;
  founderEmail?: string;
  minimumTicket?: number;
  syndicatesAllowed?: boolean;
  profileSource?: 'deck' | 'questionnaire' | 'manual' | 'directory';
  problem?: string;
  solution?: string;
  targetCustomer?: string;
  monthlyRevenue?: number;
  userCount?: number;
  growthRate?: number;
  marketSize?: number;
  competitors?: string[];
  isActivelyRaising?: boolean;
  fundraisingHistory?: FundraisingRound[];
  dataRoomConnected?: boolean;
  dataRoomProvider?: 'google' | 'dropbox' | 'docsend';
  dataRoomFolderId?: string;
  isSerialFounder?: boolean;
  previousVentures?: string[];
  updates?: StartupUpdate[];
  swissVerification?: {
    zefix?: ZefixCompanyData;
    verifiedAt?: string;
    confidence?: number;
  };
  startuptickerData?: {
    profile?: StartuptickerProfileData;
    enrichedAt?: string;
    confidence?: number;
  };
  websiteEnrichment?: {
    data?: WebsiteEnrichmentData;
    enrichedAt?: string;
    reviewed?: boolean;
  };
}

// Investor profile types
export interface InvestorBio {
  headline?: string;
  bio?: string;
  previousRoles?: { title: string; company: string; years?: string }[];
}

export interface InvestmentThesis {
  thesisStatement?: string;
  sweetSpot?: string;
  sectors: string[];
  stageCheckSizes?: { stage: string; min: number; max: number }[];
  businessModelPreferences?: string[];
  teamPreferences?: string[];
  dealBreakers?: string[];
}

export interface InvestorValueAdd {
  expertiseAreas: string[];
  description?: string;
  officeHours?: boolean;
  boardExperience?: number;
}

export interface InvestorEngagement {
  responseRate?: number;
  avgResponseTime?: string;
  introRequestsAccepted?: number;
  activePlatformUser?: boolean;
}

export interface InvestorDDActivity {
  dataRoomRequests30d?: number;
  ddInProgress?: number;
  avgDecisionDays?: number;
  conversionRate?: number;
}

export interface InvestorPatterns {
  avgCheckSize?: number;
  followOnRate?: number;
  avgTimeToTermSheet?: string;
  leadVsFollow?: 'lead' | 'follow' | 'both';
  syndicateParticipation?: boolean;
}

export interface FounderTestimonial {
  id: string;
  founderName: string;
  companyName: string;
  rating: number;
  text: string;
  date: string;
  verified: boolean;
}

export interface InvestorSocialLinks {
  twitter?: string;
  website?: string;
  calendly?: string;
  substack?: string;
}

export type InvestorBadgeType =
  | 'linkedin-verified'
  | 'serial-investor'
  | 'lead-investor'
  | 'quick-responder'
  | 'first-check'
  | 'follow-on-champion'
  | 'founder-favorite'
  | 'board-expert'
  | 'sector-specialist'
  | 'reliable-committer';

export interface InvestorBadge {
  type: InvestorBadgeType;
  earnedAt?: string;
  detail?: string;
}

export type InvestorType = 'angel' | 'vc' | 'family_office' | 'corporate' | 'syndicate';

export interface InvestorProfile {
  id: string;
  firm?: string;
  /** Investor capacity (identity step). Undefined = not yet captured — never defaulted. */
  investorType?: InvestorType;
  /** Functional role/headline (identity step), e.g. "Partner", "Managing Director". */
  role?: string;
  investmentFocus: string[];
  stagePreference: string[];
  /** Target geographies (thesis step, persisted to investor_profiles.geographies). */
  geographies?: string[];
  ticketSize: { min: number; max: number };
  /** Ticket currency (thesis step). Display unit for min/max; defaults to CHF. */
  ticketCurrency?: string;
  /** Lead / follow / both preference (thesis step, optional). */
  leadPreference?: 'lead' | 'follow' | 'both';
  location: string;
  portfolioCount: number;
  linkedInVerified?: boolean;
  linkedInUrl?: string;
  linkedInVerifiedAt?: string;
  investmentHistory?: InvestmentRecord[];
  portfolioClaims?: PortfolioClaim[];
  seniorityLevel?: 'emerging' | 'experienced' | 'veteran';
  yearsInvesting?: number;
  focusAreas?: string[];
  riskTolerance?: 'low' | 'medium' | 'high';
  investmentStages?: string[];
  bio?: InvestorBio;
  thesis?: InvestmentThesis;
  valueAdd?: InvestorValueAdd;
  engagement?: InvestorEngagement;
  ddActivity?: InvestorDDActivity;
  patterns?: InvestorPatterns;
  socialLinks?: InvestorSocialLinks;
  /**
   * REMOVED — badges are derived, never stored.
   *
   * This field had no column, no mapper and no writer; its only source was a
   * fabricated array in mock-data. A stored badge is also a badge an investor
   * could grant themselves, which "Founder Favorite" must never be. See
   * lib/investor/badges.ts.
   */
}

export interface UserInfo {
  /**
   * AUTH user id (auth.users.id), branded so it cannot be confused with
   * investor_profiles.id — the two are both uuids and the mix-up is silent.
   * See src/lib/ids.ts.
   */
  id?: AuthUserId;
  name: string;
  email: string;
  role?: string;
  avatarUrl?: string;
  linkedIn?: string;
  bio?: string;
  location?: string;
  phone?: string;
}

export type TeamMemberRole = 'founder' | 'co-founder' | 'investor_relations' | 'other';

export interface StartupTeamMember {
  id: string;
  name: string;
  email: string;
  role: TeamMemberRole;
  title: string;
  addedDate: string;
  addedBy: string;
}

// ============================================================================
// Store Interface
// ============================================================================

interface ProfileState {
  // Hydration tracking
  _hasHydrated: boolean;

  // User identity
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;

  // Ephemeral: carries the role selected on the auth page
  pendingAuthRole: 'startup' | 'investor' | null;
  setPendingAuthRole: (role: 'startup' | 'investor' | null) => void;

  // User personal info
  user: UserInfo | null;
  setUser: (user: UserInfo | null) => void;

  // Onboarding
  startupOnboarding: OnboardingProgress | null;
  investorOnboarding: OnboardingProgress | null;
  initializeOnboarding: (role: 'startup' | 'investor') => void;
  setOnboardingStep: (step: OnboardingStep) => void;
  completeOnboardingStep: (step: OnboardingStep) => void;
  skipOnboardingStep: (step: OnboardingStep) => void;
  setProfileMethod: (method: 'deck' | 'questionnaire') => void;
  completeOnboarding: () => void;
  isOnboardingComplete: () => boolean;
  isStepSkippable: (step: OnboardingStep) => boolean;
  getProfileCompleteness: () => number;

  // Startup profile
  startupProfile: StartupProfile | null;
  setStartupProfile: (profile: StartupProfile) => void;
  addStartupUpdate: (update: Omit<StartupUpdate, 'id' | 'date'>) => void;

  // Swiss Company Verification
  setSwissVerification: (verification: StartupProfile['swissVerification']) => void;
  setStartuptickerData: (data: StartupProfile['startuptickerData']) => void;
  setWebsiteEnrichment: (enrichment: StartupProfile['websiteEnrichment']) => void;

  // Verification Requests (Startup receives from Investors)
  verificationRequests: VerificationRequest[];
  setVerificationRequests: (requests: VerificationRequest[]) => void;
  respondToVerification: (requestId: string, response: 'verified' | 'rejected') => void;

  // Investor profile
  investorProfile: InvestorProfile | null;
  setInvestorProfile: (profile: InvestorProfile) => void;

  // Team Management (Startup)
  teamMembers: StartupTeamMember[];
  setTeamMembers: (members: StartupTeamMember[]) => void;
  addTeamMember: (member: StartupTeamMember) => void;
  removeTeamMember: (memberId: string) => void;

  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  _hasHydrated: false,
  userRole: null as UserRole,
  pendingAuthRole: null as 'startup' | 'investor' | null,
  user: null as UserInfo | null,
  startupOnboarding: null as OnboardingProgress | null,
  investorOnboarding: null as OnboardingProgress | null,
  startupProfile: null as StartupProfile | null,
  investorProfile: null as InvestorProfile | null,
  verificationRequests: [] as VerificationRequest[],
  teamMembers: [] as StartupTeamMember[],
};

// ============================================================================
// Store
// ============================================================================

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUserRole: (role) => set({ userRole: role }),
      setPendingAuthRole: (role) => set({ pendingAuthRole: role }),
      setUser: (user) => set({ user }),

      // Onboarding actions
      initializeOnboarding: (role) => {
        const progress: OnboardingProgress = {
          currentStep: 'welcome',
          completedSteps: [],
          skippedSteps: [],
          startedAt: new Date().toISOString(),
        };
        if (role === 'startup') {
          set({ startupOnboarding: progress });
        } else {
          set({ investorOnboarding: progress });
        }
      },

      setOnboardingStep: (step) => {
        const { userRole, startupOnboarding, investorOnboarding } = get();
        if (userRole === 'startup' && startupOnboarding) {
          set({ startupOnboarding: { ...startupOnboarding, currentStep: step } });
        } else if (userRole === 'investor' && investorOnboarding) {
          set({ investorOnboarding: { ...investorOnboarding, currentStep: step } });
        }
      },

      completeOnboardingStep: (step) => {
        const { userRole, startupOnboarding, investorOnboarding } = get();
        if (userRole === 'startup' && startupOnboarding) {
          const completedSteps = startupOnboarding.completedSteps.includes(step)
            ? startupOnboarding.completedSteps
            : [...startupOnboarding.completedSteps, step];
          set({ startupOnboarding: { ...startupOnboarding, completedSteps } });
        } else if (userRole === 'investor' && investorOnboarding) {
          const completedSteps = investorOnboarding.completedSteps.includes(step)
            ? investorOnboarding.completedSteps
            : [...investorOnboarding.completedSteps, step];
          set({ investorOnboarding: { ...investorOnboarding, completedSteps } });
        }
      },

      setProfileMethod: (method) => {
        const { startupOnboarding } = get();
        if (startupOnboarding) {
          set({ startupOnboarding: { ...startupOnboarding, profileMethod: method } });
        }
      },

      completeOnboarding: () => {
        const { userRole, startupOnboarding, investorOnboarding } = get();
        const completedAt = new Date().toISOString();
        if (userRole === 'startup' && startupOnboarding) {
          set({
            startupOnboarding: {
              ...startupOnboarding,
              currentStep: 'complete',
              completedAt,
            },
          });
        } else if (userRole === 'investor' && investorOnboarding) {
          set({
            investorOnboarding: {
              ...investorOnboarding,
              currentStep: 'complete',
              completedAt,
            },
          });
        }
      },

      isOnboardingComplete: () => {
        const { userRole, startupOnboarding, investorOnboarding } = get();
        if (userRole === 'startup') {
          return startupOnboarding?.completedAt != null;
        } else if (userRole === 'investor') {
          return investorOnboarding?.completedAt != null;
        }
        return false;
      },

      skipOnboardingStep: (step) => {
        const { userRole, startupOnboarding, investorOnboarding } = get();
        if (userRole === 'startup' && startupOnboarding) {
          const skippedSteps = startupOnboarding.skippedSteps.includes(step)
            ? startupOnboarding.skippedSteps
            : [...startupOnboarding.skippedSteps, step];
          set({ startupOnboarding: { ...startupOnboarding, skippedSteps } });
        } else if (userRole === 'investor' && investorOnboarding) {
          const skippedSteps = investorOnboarding.skippedSteps.includes(step)
            ? investorOnboarding.skippedSteps
            : [...investorOnboarding.skippedSteps, step];
          set({ investorOnboarding: { ...investorOnboarding, skippedSteps } });
        }
      },

      isStepSkippable: (step) => {
        const { userRole } = get();
        if (userRole === 'startup') {
          return STARTUP_OPTIONAL_STEPS.includes(step);
        } else if (userRole === 'investor') {
          return INVESTOR_OPTIONAL_STEPS.includes(step);
        }
        return false;
      },

      getProfileCompleteness: () => {
        const { userRole, startupOnboarding, investorOnboarding } = get();
        if (userRole === 'startup' && startupOnboarding) {
          const allSteps = [...STARTUP_REQUIRED_STEPS, ...STARTUP_OPTIONAL_STEPS];
          const totalSteps = allSteps.length;
          const completedCount = startupOnboarding.completedSteps.filter(
            s => allSteps.includes(s)
          ).length;
          return Math.round((completedCount / totalSteps) * 100);
        } else if (userRole === 'investor' && investorOnboarding) {
          const allSteps = [...INVESTOR_REQUIRED_STEPS, ...INVESTOR_OPTIONAL_STEPS];
          const totalSteps = allSteps.length;
          const completedCount = investorOnboarding.completedSteps.filter(
            s => allSteps.includes(s)
          ).length;
          return Math.round((completedCount / totalSteps) * 100);
        }
        return 0;
      },

      setStartupProfile: (profile) => set({ startupProfile: profile }),
      addStartupUpdate: (update) => {
        const { startupProfile } = get();
        if (!startupProfile) return;
        const newUpdate: StartupUpdate = {
          ...update,
          id: `upd_${Date.now()}`,
          date: new Date().toISOString(),
        };
        set({
          startupProfile: {
            ...startupProfile,
            updates: [...(startupProfile.updates || []), newUpdate],
          },
        });
      },

      // Swiss Company Verification
      setSwissVerification: (verification) => {
        set((state) => ({
          startupProfile: state.startupProfile
            ? { ...state.startupProfile, swissVerification: verification }
            : null,
        }));
      },

      setStartuptickerData: (data) => {
        set((state) => ({
          startupProfile: state.startupProfile
            ? { ...state.startupProfile, startuptickerData: data }
            : null,
        }));
      },

      setWebsiteEnrichment: (enrichment) => {
        set((state) => ({
          startupProfile: state.startupProfile
            ? { ...state.startupProfile, websiteEnrichment: enrichment }
            : null,
        }));
      },

      // Verification Requests
      setVerificationRequests: (requests) => set({ verificationRequests: requests }),
      respondToVerification: (requestId, response) => set({
        verificationRequests: get().verificationRequests.map(r =>
          r.id === requestId
            ? { ...r, status: response, respondedAt: new Date().toISOString() }
            : r
        ),
      }),

      setInvestorProfile: (profile) => set({ investorProfile: profile }),

      // Team Management
      setTeamMembers: (members) => set({ teamMembers: members }),
      addTeamMember: (member) => set({ teamMembers: [...get().teamMembers, member] }),
      removeTeamMember: (memberId) => set({
        teamMembers: get().teamMembers.filter(m => m.id !== memberId),
      }),

      reset: () => set({ ...initialState, _hasHydrated: true }),
    }),
    {
      name: 'inverio-profile',
      // v2 (Phase 110 / RM-T1-18): userRole evicted from persisted slice. Bumping the
      // version invalidates v1 caches cleanly so existing users don't carry a stale
      // persisted role across upgrade. In-memory userRole is repopulated by the auth
      // listener / login flow which calls setUserRole() on session restore.
      version: 2,
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        // userRole intentionally omitted (Phase 110 RM-T1-18): role is server-fetched per
        // layout mount via useUserRole. In-memory state retained for onboarding-step
        // routing only; never persisted, so a devtools-edit of localStorage cannot
        // bypass any auth gate.
        user: state.user,
        startupProfile: state.startupProfile,
        investorProfile: state.investorProfile,
        startupOnboarding: state.startupOnboarding,
        investorOnboarding: state.investorOnboarding,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          reportError(error, { context: 'ProfileStore.rehydration', silent: true });
        }
        // FIX (issue #13): _hasHydrated is set in the onRehydrateStorage
        // callback, which fires AFTER zustand has loaded persisted state.
        // Deferred to next microtask because onRehydrateStorage fires during
        // create() before useProfileStore is assigned (circular reference).
        queueMicrotask(() => {
          useProfileStore.setState({ _hasHydrated: true });
        });
      },
    }
  )
);
